import { Box, Button, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import { useStyles } from "./styles"
import ValueList from "../valuesList"
import { forwardRef, memo, Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { ListFormProps, ListFormRef } from "./types"
import CatalogValues from "../catalogValues"
import { ViewMode } from "../../enums/enums"
import Papa from 'papaparse';
import { LoadingButton } from "@mui/lab"
import Loader from "../../../../../../components/loader"


const ListFormBase = ({ id, loadingItem, setTouched, loadingSubmit, touched, editName, editValues, mode, setMode, setModal, closeModal, hadleSubmit, setIdEdit }: ListFormProps, ref: Ref<ListFormRef>) => {
    const { t } = useTranslation()
    const styles = useStyles()
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const [name, setName] = useState<string | null>(editName ?? '')
    const [values, setValues] = useState<{ id: number, value: string }[]>(editValues ?? [])
    const valueListRef = useRef<{ clearValue: () => void } | null>(null);

    useImperativeHandle(ref, () => ({
        hasData: () => !!name?.trim() || values.length > 0,
        clear: () => {
            setName('');
            setValues([]);
            setMode(ViewMode.CREATE);
            valueListRef.current?.clearValue();
        }
    }));

    const addValue = useCallback((newValue: string) => {
        setValues(prev => [
            ...prev,
            {
                id: prev.length ? prev[prev.length - 1].id + 1 : 1,
                value: newValue,
            },
        ]);
    }, []);

    const deleteValue = useCallback((id: number) => {
        setValues(prevValues => prevValues.filter(item => item.id !== id));
    }, []);

    const handleName = useCallback((newValue: string) => {
        setName(newValue);
    }, []);

    const submit = () => {
        hadleSubmit(String(name), values)
    }

    const cancelSubmit = () => {
        if (!!name || values.length > 0 || mode == ViewMode.EDIT) {
            setModal(prev => ({
                ...prev,
                open: true,
                title: t('dropList.modal.titles.confirmation'),
                description: t(`dropList.modal.descriptions.${mode == ViewMode.CREATE ? 'cancelCreate' : 'cancelEdition'}`),
                confirm: () => {
                    setName('')
                    setValues([])
                    setMode(ViewMode.CREATE)
                    setTouched(false)
                    setIdEdit(undefined)
                    valueListRef.current?.clearValue();
                    closeModal()
                }
            }))
        }
    }

    const handleCSVImport = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const arrayBuffer = e.target?.result as ArrayBuffer;
                if (!arrayBuffer) return;

                const bytes = new Uint8Array(arrayBuffer);

                // 1) Detectar BOM UTF-8 (EF BB BF)
                const hasUtf8Bom = bytes.length >= 3 &&
                    bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf;

                // Helper: decodificar con TextDecoder
                const decode = (encoding: string) => {
                    try {
                        return new TextDecoder(encoding).decode(bytes);
                    } catch (err) {
                        console.warn("TextDecoder no soporta encoding:", encoding, err);
                        return null;
                    }
                };

                // 2) Intentar UTF-8 inicialmente (si BOM => preferir)
                let text = hasUtf8Bom ? decode("utf-8") || "" : decode("utf-8") || "";

                // 3) Detectar mojibake común (ejemplos: Ã±, Ã¡, Ã©, Â¿, Â¡)
                const mojibakeRegex = /Ã[¡-ñ]|Â[¡¿]|�/; // patrón simple para caracteres mojibake o replacement char
                const looksLikeMojibake = mojibakeRegex.test(text);

                if (looksLikeMojibake) {
                    // 4) Reintentar con windows-1252 / iso-8859-1
                    const alt = decode("windows-1252") || decode("iso-8859-1") || "";
                    if (alt && !mojibakeRegex.test(alt)) {
                        text = alt;
                    } else {
                        // si sigue raro, intenta ambos y elige el que parezca mejor (más vocales acentuadas / ñ)
                        const utf8Count = (text.match(/[áéíóúÁÉÍÓÚñÑ]/g) || []).length;
                        const altCount = (alt.match(/[áéíóúÁÉÍÓÚñÑ]/g) || []).length;
                        if (altCount > utf8Count) text = alt;
                    }
                }

                // 5) Parsear con PapaParse a partir del texto decodificado
                Papa.parse<string[]>(text, {
                    header: false,
                    skipEmptyLines: true,
                    complete: (results) => {
                        const flatValues = (results.data as string[][]).flat();
                        const filtered = flatValues.filter((v) => v && v.trim() !== "");
                        const importedValues = filtered.map((value) => ({
                            id: Math.random(),
                            value,
                        }));
                        setValues((prev) => [...prev, ...importedValues]);
                    },
                    error: (err: Error) => {
                        console.error("Papa parse error:", err);
                    },
                });
            } catch (err) {
                console.error("Error leyendo/decodificando CSV:", err);
            }
        };

        // Leemos como ArrayBuffer para permitir TextDecoder con distintos encodings
        reader.readAsArrayBuffer(file);

        // permitir seleccionar el mismo archivo después
        event.target.value = "";
    };

    useEffect(() => {
        setName(editName ?? '');
        setValues(editValues ?? []);
    }, [editName, editValues]);

    return (
        <Box sx={styles.mainContainer}>
            <Typography sx={styles.title} variant="blue_title">{t(`dropList.titles.${mode == ViewMode.CREATE ? 'newDropList' : 'editDropList'}`)}</Typography>

            {loadingItem ?
                <Loader />
                :
                <Box sx={styles.body}>
                    <ValueList ref={valueListRef} setTouched={setTouched} touched={touched} name={name} handleName={handleName} addValue={addValue} />

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        style={{ display: "none" }}
                        onChange={handleCSVImport}
                    />

                    <Box sx={styles.containerImpor}>
                        <Button sx={styles.subtitle} onClick={() => { fileInputRef.current?.click() }}>{t('general.importCsv')}</Button>
                    </Box>

                    <CatalogValues deleteValue={deleteValue} values={values} />
                </Box>
            }

            <Box sx={styles.buttonsContainer}>
                <Button sx={styles.subtitle} onClick={() => { cancelSubmit() }}>{t('general.cancel')}</Button>
                <LoadingButton loading={loadingSubmit} disabled={(name?.trim().length ?? 0) < 3|| !values.length} className="next" variant="contained" onClick={() => { submit() }}>{mode == ViewMode.EDIT ? t('general.edit') : t('general.save')}</LoadingButton>
            </Box>

        </Box>
    )
}

const ListForm = memo(forwardRef(ListFormBase));
export default ListForm;